import type React from 'react';
import { useCallback } from 'react';

import { Dialog, DialogContent, DialogActions, Typography, Stack, useTheme } from '@mui/material';
import { CopyToClipboardButton, OrdButton } from '@ragu-components/ord-components';
import { DateTime } from 'luxon';
import { useTranslate } from 'react-admin';

import type { PersonalAccessTokenCreateResponse } from '@ragu-components/ord-types';

export interface PersonalAccessTokensInfoDialogProps {
  dialogContent?: PersonalAccessTokenCreateResponse;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const PersonalAccessTokensInfoDialog = ({
  dialogContent = undefined,
  open,
  setOpen,
}: PersonalAccessTokensInfoDialogProps): React.ReactElement => {
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const theme = useTheme();
  const translate = useTranslate();

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent>
        <Stack>
          <Typography variant='body1' sx={{ marginBottom: theme.spacing(2) }}>
            {translate('resources.me/personal_access_tokens.dialogContent')}
          </Typography>
          <Stack direction='row' alignItems='center' width={'100%'} height={theme.spacing(5)}>
            <Typography variant='body2' fontWeight={'bold'} whiteSpace='pre'>
              {`${translate('resources.me/personal_access_tokens.fields.name')}: `}
            </Typography>
            <Typography variant='body2' textOverflow={'ellipsis'} fontFamily='monospace'>
              {dialogContent?.name}
            </Typography>
          </Stack>
          <Stack direction='row' alignItems='center' width={'100%'} height={theme.spacing(5)}>
            <Typography variant='body2' fontWeight={'bold'} whiteSpace='pre'>
              {`${translate('resources.me/personal_access_tokens.fields.expires_at')}: `}
            </Typography>
            {dialogContent?.expires_at !== null && dialogContent?.expires_at !== undefined && (
              <Typography variant='body2' textOverflow={'ellipsis'} fontFamily='monospace'>
                {DateTime.fromFormat(dialogContent.expires_at, 'yyyy-MM-dd').toLocaleString()}
              </Typography>
            )}
          </Stack>
          <Stack direction='row' alignItems='center' width={'100%'} height={theme.spacing(5)}>
            <Typography variant='body2' fontWeight={'bold'} whiteSpace='pre'>
              {`${translate('resources.me/personal_access_tokens.fields.scopes')}: `}
            </Typography>
            {dialogContent?.scopes.map((scope: string) => (
              <Typography
                key={scope}
                variant='body2'
                textOverflow={'ellipsis'}
                fontFamily='monospace'
                sx={{ mx: theme.spacing(0.5) }}
              >
                {scope}
              </Typography>
            ))}
          </Stack>
          <Stack direction='row' alignItems='flex-start' width={'100%'} justifyContent='space-between'>
            <Stack direction='row' alignItems='center' height={theme.spacing(5)}>
              <Typography variant='body2' fontWeight={'bold'} whiteSpace='pre'>
                {`${translate('resources.me/personal_access_tokens.fields.token')}: `}
              </Typography>
              <Typography variant='body2' textOverflow={'ellipsis'} fontFamily='monospace' color='primary'>
                {dialogContent?.token}
              </Typography>
            </Stack>
            <CopyToClipboardButton text={dialogContent?.token} feedback='embeded' />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <OrdButton
          onClick={handleClose}
          color='primary'
          variant='text'
          sx={{ width: 'fit-content', marginRight: theme.spacing(1) }}
        >
          Ok
        </OrdButton>
      </DialogActions>
    </Dialog>
  );
};
